// src/components/CustomerSummary/components/SectionCard.jsx
// ============================================================================
// BLOCK CS.UI.2 — SectionCard
// ----------------------------------------------------------------------------
// PURPOSE
// - Render one Customer Summary section inside a collapsible card.
// - Provide a shared toolbar (expand/lock/auto/clear/reorder) and route each
//   sectionKey to its correct renderer (table or raw text).
//
// LINT / STYLE CONSTRAINTS
// - Avoid inline lambdas in JSX (react/jsx-no-bind).
// - Parent passes UNBOUND actions; this component binds them with sectionKey.
// - Derived sections are read-only (no lock/auto/clear/text editing).
//
// DATA FLOW
// - value: current section text (string)
// - uiPrefs.tables[tableKey]: optional persisted column widths for table UIs
// - onSetTableColumnWidths(tableKey, widths): controller handler for persistence
// ============================================================================

import React from 'react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import AverageStudyLengthTable from './AverageStudyLengthTable.jsx';
import CollapsibleCard from './CollapsibleCard.jsx';
import DeviceUtilizationTable from './DeviceUtilizationTable.jsx';
import PatientStatsTable from './PatientStatsTable.jsx';
import PayerMixTable from './PayerMixTable.jsx';
import SectionToolbar from './SectionToolbar.jsx';
import StudiesStartedTable from './StudiesStartedTable.jsx';
import StudiesStartedWorkflowDistributionTable from './StudiesStartedWorkflowDistributionTable.jsx';
import SupportTicketsResolvedTable from './SupportTicketsResolvedTable.jsx';
import Top3OrderingProvidersTable from './Top3OrderingProvidersTable.jsx';
import Top3StudyIndicationsTable from './Top3StudyIndicationsTable.jsx';
import TurnaroundTimesTable from './TurnaroundTimesTable.jsx';
import UrgentEmergentNotificationsTable from './UrgentEmergentNotificationsTable.jsx';

export default function SectionCard({
  sectionKey,
  title,
  locked,
  expanded,
  canMoveUp,
  canMoveDown,

  // gating
  isDerived,

  // UNBOUND actions: (key) => void
  onToggleExpand,
  onToggleLock,
  onAuto,
  onClear,
  onMoveUp,
  onMoveDown,
  onChangeText,

  // ui prefs
  uiPrefs,
  onSetTableColumnWidths,
  value,
}) {
  // ==========================================================================
  // BLOCK CS.UI.2.1 — Stable action bindings (no JSX lambdas)
  // --------------------------------------------------------------------------
  // All actions are key-based at the controller layer; bind sectionKey here.
  // Derived sections: disallow lock/auto/clear/edit.
  // ==========================================================================
  const handleToggleExpand = React.useCallback(() => {
    if (typeof onToggleExpand === 'function') {
      onToggleExpand(sectionKey);
    }
  }, [onToggleExpand, sectionKey]);

  const handleToggleLock = React.useCallback(() => {
    if (isDerived) {
      return;
    }
    if (typeof onToggleLock === 'function') {
      onToggleLock(sectionKey);
    }
  }, [isDerived, onToggleLock, sectionKey]);

  const handleAuto = React.useCallback(() => {
    if (isDerived) {
      return;
    }
    if (typeof onAuto === 'function') {
      onAuto(sectionKey, { force: true });
    }
  }, [isDerived, onAuto, sectionKey]);

  const handleClear = React.useCallback(() => {
    if (isDerived) {
      return;
    }
    if (typeof onClear === 'function') {
      onClear(sectionKey);
    }
  }, [isDerived, onClear, sectionKey]);

  const handleMoveUp = React.useCallback(() => {
    if (typeof onMoveUp === 'function') {
      onMoveUp(sectionKey);
    }
  }, [onMoveUp, sectionKey]);

  const handleMoveDown = React.useCallback(() => {
    if (typeof onMoveDown === 'function') {
      onMoveDown(sectionKey);
    }
  }, [onMoveDown, sectionKey]);

  const handleChangeText = React.useCallback(
    (nextText) => {
      if (isDerived) {
        return;
      }
      if (typeof onChangeText === 'function') {
        onChangeText(sectionKey, nextText);
      }
    },
    [isDerived, onChangeText, sectionKey]
  );

  // ==========================================================================
  // BLOCK CS.UI.2.1.1 — Stable TextField onChange (no JSX lambdas)
  // ==========================================================================
  const handleTextFieldChange = React.useCallback(
    (e) => {
      if (isDerived) {
        return;
      }
      handleChangeText(e.target.value);
    },
    [handleChangeText, isDerived]
  );

  // ==========================================================================
  // BLOCK CS.UI.2.1.2 — Toolbar header composition
  // --------------------------------------------------------------------------
  // Derived sections: hide lock/auto/clear actions by passing undefined.
  // ==========================================================================
  const header = (
    <SectionToolbar
      title={title}
      locked={locked}
      expanded={expanded}
      canMoveUp={canMoveUp}
      canMoveDown={canMoveDown}
      onToggleExpand={handleToggleExpand}
      onToggleLock={isDerived ? undefined : handleToggleLock}
      onAuto={isDerived ? undefined : handleAuto}
      onClear={isDerived ? undefined : handleClear}
      onMoveUp={handleMoveUp}
      onMoveDown={handleMoveDown}
    />
  );

  // ==========================================================================
  // BLOCK CS.UI.2.2 — Section routing flags + persisted widths lookup
  // --------------------------------------------------------------------------
  // GOAL
  // - Keep routing rules centralized and readable.
  // - Keep uiPrefs table keys explicit and consistent.
  // ==========================================================================
  const isAverageStudyLength = sectionKey === 'averageStudyLength';
  const isTurnaroundTimes = sectionKey === 'turnaroundTimes';
  const isUrgentEmergentNotifications = sectionKey === 'urgentEmergentNotifications';
  const isDeviceUtilization = sectionKey === 'deviceUtilization';

  // Final report sections
  const isSupportTicketsResolved = sectionKey === 'supportTicketsResolved';
  const isTop3StudyIndications = sectionKey === 'top3StudyIndications';
  const isTop3OrderingProviders = sectionKey === 'top3OrderingProviders';
  const isPayerMix = sectionKey === 'payerMix';
  const isPatientStats = sectionKey === 'patientStats';

  const isStudiesTable = sectionKey === 'studiesStarted' || sectionKey === 'finalizedStudies';

  const isWorkflowDistribution =
    sectionKey === 'studiesStartedWorkflowDistribution' ||
    sectionKey === 'finalizedStudiesWorkflowDistributions';

  // Table widths (when supported by the table component)
  const aslWidths = uiPrefs?.tables?.averageStudyLength;
  const tatWidths = uiPrefs?.tables?.turnaroundTimes;
  const uenWidths = uiPrefs?.tables?.urgentEmergentNotifications;
  const duWidths = uiPrefs?.tables?.deviceUtilization;

  // Final report widths
  const strWidths = uiPrefs?.tables?.supportTicketsResolved;
  const indWidths = uiPrefs?.tables?.top3StudyIndications;
  const topProvWidths = uiPrefs?.tables?.top3OrderingProviders;
  const payWidths = uiPrefs?.tables?.payerMix;
  const psWidths = uiPrefs?.tables?.patientStats;

  // Studies / workflow tables have different persistence buckets by section key.
  let studiesTableKey = '';
  if (sectionKey === 'studiesStarted') {
    studiesTableKey = 'studiesStarted';
  } else if (sectionKey === 'finalizedStudies') {
    studiesTableKey = 'finalizedStudies';
  }

  let workflowTableKey = '';
  if (sectionKey === 'studiesStartedWorkflowDistribution') {
    workflowTableKey = 'studiesStartedWorkflowDistribution';
  } else if (sectionKey === 'finalizedStudiesWorkflowDistributions') {
    workflowTableKey = 'finalizedStudiesWorkflowDistributions';
  }

  const studiesWidths = studiesTableKey ? uiPrefs?.tables?.[studiesTableKey] : null;
  const workflowWidths = workflowTableKey ? uiPrefs?.tables?.[workflowTableKey] : null;

  // ==========================================================================
  // BLOCK CS.UI.2.3 — Stable column width persistence handlers
  // --------------------------------------------------------------------------
  // PURPOSE
  // - Keep all width persistence writes going through onSetTableColumnWidths().
  // - Avoid JSX lambdas by passing stable callbacks into table components.
  // ==========================================================================
  const handleAslWidthsChange = React.useCallback(
    (nextWidths) => {
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths('averageStudyLength', nextWidths);
      }
    },
    [onSetTableColumnWidths]
  );

  const handleTatWidthsChange = React.useCallback(
    (nextWidths) => {
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths('turnaroundTimes', nextWidths);
      }
    },
    [onSetTableColumnWidths]
  );

  const handleUenWidthsChange = React.useCallback(
    (nextWidths) => {
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths('urgentEmergentNotifications', nextWidths);
      }
    },
    [onSetTableColumnWidths]
  );

  const handleDuWidthsChange = React.useCallback(
    (nextWidths) => {
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths('deviceUtilization', nextWidths);
      }
    },
    [onSetTableColumnWidths]
  );

  // Final report handlers
  const handleStrWidthsChange = React.useCallback(
    (nextWidths) => {
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths('supportTicketsResolved', nextWidths);
      }
    },
    [onSetTableColumnWidths]
  );

  const handleIndWidthsChange = React.useCallback(
    (nextWidths) => {
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths('top3StudyIndications', nextWidths);
      }
    },
    [onSetTableColumnWidths]
  );

  const handleTopProvWidthsChange = React.useCallback(
    (nextWidths) => {
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths('top3OrderingProviders', nextWidths);
      }
    },
    [onSetTableColumnWidths]
  );

  const handlePayWidthsChange = React.useCallback(
    (nextWidths) => {
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths('payerMix', nextWidths);
      }
    },
    [onSetTableColumnWidths]
  );

  const handlePsWidthsChange = React.useCallback(
    (nextWidths) => {
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths('patientStats', nextWidths);
      }
    },
    [onSetTableColumnWidths]
  );

  const handleStudiesWidthsChange = React.useCallback(
    (nextWidths) => {
      if (!studiesTableKey) {
        return;
      }
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths(studiesTableKey, nextWidths);
      }
    },
    [onSetTableColumnWidths, studiesTableKey]
  );

  const handleWorkflowWidthsChange = React.useCallback(
    (nextWidths) => {
      if (!workflowTableKey) {
        return;
      }
      if (typeof onSetTableColumnWidths === 'function') {
        onSetTableColumnWidths(workflowTableKey, nextWidths);
      }
    },
    [onSetTableColumnWidths, workflowTableKey]
  );

  // ==========================================================================
  // BLOCK CS.UI.2.4 — Content renderer (sectionKey -> component)
  // --------------------------------------------------------------------------
  // RULES
  // - StudiesStartedTable is used for BOTH studiesStarted and finalizedStudies.
  // - Workflow distribution table is read-only derived output.
  // - Unknown keys fall back to a multiline TextField (editable unless locked/derived).
  //
  // NOTE
  // - This renderer is only used locally (not passed as a prop), so useCallback
  //   provides no benefit and adds a large, brittle dependency list.
  // ==========================================================================
  function renderContent() {
    if (isStudiesTable) {
      return (
        <StudiesStartedTable
          text={value}
          locked={locked}
          onChangeText={handleChangeText}
          columnWidths={studiesWidths}
          onColumnWidthsChange={handleStudiesWidthsChange}
        />
      );
    }

    if (isTurnaroundTimes) {
      return (
        <TurnaroundTimesTable
          text={value}
          columnWidths={tatWidths}
          onColumnWidthsChange={handleTatWidthsChange}
        />
      );
    }

    if (isUrgentEmergentNotifications) {
      return (
        <UrgentEmergentNotificationsTable
          text={value}
          columnWidths={uenWidths}
          onColumnWidthsChange={handleUenWidthsChange}
        />
      );
    }

    // ======================================================================
    // BLOCK CS.UI.2.4.1 — Device Utilization (read-only + resizable columns)
    // ----------------------------------------------------------------------
    // NOTES
    // - Read-only table output generated by server.
    // - Column widths persisted in uiPrefs.tables.deviceUtilization.
    // ======================================================================
    if (isDeviceUtilization) {
      return (
        <DeviceUtilizationTable
          text={value}
          locked={locked}
          onChangeText={handleChangeText}
          columnWidths={duWidths}
          onColumnWidthsChange={handleDuWidthsChange}
        />
      );
    }

    if (isAverageStudyLength) {
      return (
        <AverageStudyLengthTable
          text={value}
          columnWidths={aslWidths}
          onColumnWidthsChange={handleAslWidthsChange}
        />
      );
    }

    if (isWorkflowDistribution) {
      return (
        <StudiesStartedWorkflowDistributionTable
          text={value}
          columnWidths={workflowWidths}
          onColumnWidthsChange={handleWorkflowWidthsChange}
        />
      );
    }

    if (isWorkflowDistribution) {
      return (
        <StudiesStartedWorkflowDistributionTable
          text={value}
          columnWidths={workflowWidths}
          onColumnWidthsChange={handleWorkflowWidthsChange}
        />
      );
    }

    // ======================================================================
    // BLOCK CS.UI.2.4.2 — Final report sections (read-only + resizable)
    // ======================================================================
    if (isSupportTicketsResolved) {
      return (
        <SupportTicketsResolvedTable
          text={value}
          columnWidths={strWidths}
          onColumnWidthsChange={handleStrWidthsChange}
        />
      );
    }

    if (isTop3StudyIndications) {
      return (
        <Top3StudyIndicationsTable
          text={value}
          columnWidths={indWidths}
          onColumnWidthsChange={handleIndWidthsChange}
        />
      );
    }

    if (isTop3OrderingProviders) {
      return (
        <Top3OrderingProvidersTable
          text={value}
          columnWidths={topProvWidths}
          onColumnWidthsChange={handleTopProvWidthsChange}
        />
      );
    }

    if (isPayerMix) {
      return (
        <PayerMixTable
          text={value}
          columnWidths={payWidths}
          onColumnWidthsChange={handlePayWidthsChange}
        />
      );
    }

    if (isPatientStats) {
      return (
        <PatientStatsTable
          text={value}
          columnWidths={psWidths}
          onColumnWidthsChange={handlePsWidthsChange}
        />
      );
    }

    // Default: editable raw text (unless locked or derived).
    return (
      <TextField
        fullWidth
        multiline
        minRows={6}
        value={value}
        disabled={locked || isDerived}
        onChange={handleTextFieldChange}
      />
    );
  }

  return (
    <CollapsibleCard header={header} expanded={expanded}>
      <Box sx={{ mt: 1 }}>{renderContent()}</Box>
    </CollapsibleCard>
  );
}
